import React, { useCallback, useEffect, useRef } from "react";
import { Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DEF_EDGE_THICKNESS } from "./constants";
import {
  EdgeLabel,
  EdgeLabelObject,
  EdgeLabelWrapper,
  SVGContainer,
} from "./style_vanilla.css";
import { EdgeConfig, TreeConfig } from "./types";
import { setSVGbounds, setNodeEdges } from "./util";

/**
 * Component to lay the lines between two nodes in the tree. It also renders the label
 * for the edge.
 *
 * @param {edgeConfigs, treeConfig, containerBounds}
 * @returns Component that renders the edges/connecting lines between nodes in the tree with label
 */
export const TreeEdge: React.FC<{
  edgeConfigs: Array<EdgeConfig>;
  treeConfig: TreeConfig;
  containerBounds?: DOMRect | null;
}> = ({ edgeConfigs, treeConfig, containerBounds }) => {
  const svgContainer = useRef(null);
  const svgElement = useRef(null);

  /**
   * Connect all the nodes in the tree with edges
   */
  const connectAllNodes = useCallback(() => {
    // Consider debouncing/throttling for frequent resizing
    const animationFrameHandler = requestAnimationFrame(() => {
      if (containerBounds && svgElement.current) {
        setSVGbounds(containerBounds, svgElement.current);
        setNodeEdges(containerBounds, edgeConfigs, treeConfig);
      }
    });
    return () => cancelAnimationFrame(animationFrameHandler);
  }, [edgeConfigs, containerBounds, treeConfig]);

  /**
   * Update the edge based on the component resize
   */
  useEffect(() => {
    if (svgContainer) {
      connectAllNodes();
    }
  }, [svgContainer, connectAllNodes]);

  /**
   * Render the edges between nodes in the tree
   */
  const renderEdge = useCallback((edgeConfig: EdgeConfig, index: number) => {
    const edgeStyle = edgeConfig.edgeStyle;
    return (
      <React.Fragment key={`${edgeConfig.from}-${edgeConfig.to}-${index}`}>
        <path
          id={`edge_path${index}`}
          data-testid={`edge_path${index}`}
          d="M0 0"
          stroke={edgeStyle?.color || Colors.Neutral300}
          fill="none"
          strokeWidth={`${edgeStyle?.thickness || DEF_EDGE_THICKNESS}px`}
          role="presentation"
        />
        {/* Using Foreignobject to provide support to add React element inside the SVG.
        The width, height with 100% and overflow visible would help the component to auto size based on the content.
        We would render only if the label is available*/}
        {edgeConfig.label && (
          <foreignObject
            id={`label_container${index}`}
            data-testid={`label_container${index}`}
            x="0"
            y="0"
            width="100%"
            height="100%"
            className={EdgeLabelObject}
          >
            <div
              id={`label_wrapper${index}`}
              data-testid={`label_wrapper${index}`}
              className={EdgeLabelWrapper}
            >
              {typeof edgeConfig.label === "string" ? (
                <Text variant="strong-sm" className={EdgeLabel}>
                  {edgeConfig.label}
                </Text>
              ) : (
                edgeConfig.label
              )}
            </div>
          </foreignObject>
        )}
      </React.Fragment>
    );
  }, []);

  return (
    <div className={SVGContainer} ref={svgContainer} data-testid="edge-svg-container">
      <svg ref={svgElement} data-testid="edge-svg">
        {edgeConfigs.map(renderEdge)}
      </svg>
    </div>
  );
};
